import React, { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { decode, encode } from 'js-base64';
import { notification } from 'antd';

import routes from 'routing/routes';
import buildPath from 'utils/build_path';
import useTrackingCookieConsent from 'hooks/useTrackingCookieConsent';

import Loading from 'components/loading';
import GiftCardStoreCheckout from 'components/gift_card_store_checkout/gift_card_store_checkout';
import {
  ICartItem,
  ICheckoutFormValues,
  IGiftCard,
} from 'components/gift_card_store_checkout/gift_card_store_checkout.types';

import { PropertyGiftCardsActionsContext, PropertyGiftCardsDataContext } from 'containers/data_context';

import styles from './gift_card_store_checkout_page.module.scss';

const GiftCardStoreCheckoutPage: React.FC = () => {
  const { i18n, t } = useTranslation();
  const history = useHistory();

  const { selectedProperty } = useParams<{ selectedProperty: string }>();

  const { getStoreGiftCardTypes, setGiftCardCheckoutData, clearDataFromStorage, checkCartPrices } = useContext(
    PropertyGiftCardsActionsContext,
  );
  const [cartItems, setCartItems] = useState<Array<ICartItem>>([]);
  const [promoCode, setPromoCode] = useState<string>('');
  const isTrackingAllowed: boolean = useTrackingCookieConsent();

  const {
    storeGiftCardTypes: { data, isLoading },
    propertyInfo: { data: propertyDetails },
    cartPrices: { isLoading: isCartPricesLoading },
  } = useContext(PropertyGiftCardsDataContext);

  useEffect(
    function initApp() {
      getStoreGiftCardTypes({ propertyId: selectedProperty });

      const storedPromoCode = decode(localStorage.getItem(`promo-code-${selectedProperty}`) || '');
      if (storedPromoCode) {
        setPromoCode(storedPromoCode);
      }
    },
    [getStoreGiftCardTypes],
  );

  const initializeCartItems = () => {
    const cartitem = localStorage.getItem(`cart-items-${selectedProperty}`);
    const storedPromoCode = decode(localStorage.getItem(`promo-code-${selectedProperty}`) || '');

    if (data && cartitem) {
      const cartItems = JSON.parse(decode(cartitem)).items;

      const isItemAvailable = cartItems.every((item: ICartItem) =>
        data.find((giftCard: IGiftCard) => item.item.id == giftCard.id),
      );

      if (!isItemAvailable) {
        localStorage.removeItem(`cart-items-${selectedProperty}`);
        localStorage.removeItem(`promo-code-${selectedProperty}`);

        const redirectPath = buildPath('', routes.giftCardStorePage, { selectedProperty });
        history.push(redirectPath);

        return notification['error']({
          message: t('gift_card.checkout.error_occured'),
          description: t('gift_card.checkout.sold_out'),
        });
      } else {
        const cartItems = JSON.parse(decode(cartitem)).items;
        setCartItems(cartItems);
        if (storedPromoCode) {
          applyPromoCode(cartItems);
        }
      }
    } else {
      if (!cartitem) {
        const redirectPath = buildPath('', routes.giftCardStorePage, { selectedProperty });
        history.push(redirectPath);

        return notification['error']({
          message: t('gift_card.checkout.error_occured'),
          description: t('gift_card.basket_is_empty'),
        });
      }
    }
  };

  useEffect(() => {
    initializeCartItems();
  }, [data]);

  const onSaveGiftCardInfo = async (giftCardData: ICheckoutFormValues) => {
    const giftCardParams = { ...giftCardData, locale: i18n.language };
    try {
      const response = await setGiftCardCheckoutData({ giftCardParams, propertyId: selectedProperty });
      isTrackingAllowed && window?.gtag('event', 'add_payment_info');
      if (response?.redirectUrl) {
        clearDataFromStorage();
        localStorage.removeItem(`cart-items-${selectedProperty}`);
        localStorage.removeItem(`promo-code-${selectedProperty}`);
        window.location = response?.redirectUrl;
      } else {
        const { formActionUrl, ...rest } = response;

        const params = new URLSearchParams(rest);
        window.location.href = formActionUrl + '?' + params;
        return;
      }
    } catch (e: unknown) {
      if (e instanceof Error && e?.message === 'Promo code is not valid') {
        localStorage.removeItem(`promo-code-${selectedProperty}`);
        setPromoCode('');
        initializeCartItems();
        return notification['error']({
          message: t('promo_codes.message.error'),
        });
      }
      return notification['error']({
        message: t('general.error_message'),
      });
    }
  };

  const onPromoCodeChange = (value: string) => {
    setPromoCode(value);
  };

  const applyPromoCode = async (currentCartItems = cartItems) => {
    const items = currentCartItems?.map(item => ({
      item: { id: item.item.id, netPrice: item.item.netPrice },
      quantity: item.quantity,
    }));
    try {
      const payload = {
        cartParams: {
          promoCode,
          items,
        },
      };
      const newCartItems = [...currentCartItems];
      const data = await checkCartPrices({ payload, propertyId: selectedProperty });
      if (data) {
        data?.items?.forEach((item: ICartItem) => {
          const index = newCartItems.findIndex(cartItem => cartItem.item.id === item.item.id);
          newCartItems[index].item.netPrice = item.item.netPrice;
          newCartItems[index].item.promoCodeApplied = item.promoCodeApplied;
        });
        setCartItems(newCartItems);
        if (promoCode) {
          localStorage.setItem(`promo-code-${selectedProperty}`, encode(promoCode));
          return notification['success']({
            message: t('promo_codes.message.success'),
          });
        }
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        const storedPromoCode = decode(localStorage.getItem(`promo-code-${selectedProperty}`) || '');
        if (storedPromoCode && storedPromoCode !== promoCode) {
          localStorage.removeItem(`promo-code-${selectedProperty}`);
          initializeCartItems();
        }
        return notification['error']({
          message: `${e?.message}`,
        });
      }
    }
  };

  const onApplyPromoCode = () => {
    applyPromoCode();
  };

  return (
    <div className={styles.root} data-testid="GiftCardStoreCheckoutPage">
      {!data || isLoading ? (
        <Loading />
      ) : (
        <GiftCardStoreCheckout
          cartItems={cartItems}
          onSaveGiftCardInfo={onSaveGiftCardInfo}
          onApplyPromoCode={onApplyPromoCode}
          promoCode={promoCode}
          promoCodeLoading={isCartPricesLoading}
          onPromoCodeChange={onPromoCodeChange}
          storeSettings={propertyDetails?.storeSetting}
          property={propertyDetails}
        />
      )}
    </div>
  );
};

export default GiftCardStoreCheckoutPage;
